import React, { forwardRef } from "react";
import { useLang } from "../context/LanguageContext";

// A printable invitation card. Always rendered (off-screen) so html2canvas can capture it.
const InvitationCard = forwardRef(function InvitationCard(_, ref) {
  const { t, lang } = useLang();

  return (
    <div
      ref={ref}
      id="invitation-card-pdf"
      style={{
        position: "fixed",
        top: 0,
        left: "-99999px",
        width: "794px", // A4 width @ 96dpi (210mm)
        height: "1123px", // A4 height @ 96dpi (297mm)
        background: "#FBF9F6",
        color: "#1A1514",
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        padding: "70px 80px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
      aria-hidden
    >
      {/* Decorative top frame */}
      <div
        style={{
          position: "absolute",
          inset: "32px",
          border: "1px solid #DBCDBA",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: "40px",
          border: "1px solid rgba(123,31,42,0.25)",
          pointerEvents: "none",
        }}
      />

      {/* Background blob */}
      <div
        style={{
          position: "absolute",
          width: "440px",
          height: "440px",
          top: "-120px",
          right: "-160px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(123,31,42,0.18), transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "380px",
          height: "380px",
          bottom: "-140px",
          left: "-160px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(139,90,43,0.18), transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          textAlign: "center",
        }}
      >
        {/* Top section */}
        <div style={{ width: "100%" }}>
          <p
            style={{
              fontFamily: '"Outfit", sans-serif',
              fontSize: "11px",
              letterSpacing: "0.55em",
              textTransform: "uppercase",
              color: "#7B1F2A",
              margin: 0,
              marginBottom: "22px",
              fontWeight: 500,
            }}
          >
            {{
              ro: "Vă invităm la nunta noastră",
              it: "Vi invitiamo al nostro matrimonio",
              de: "Wir laden euch zu unserer Hochzeit ein",
            }[lang] || "Vi invitiamo al nostro matrimonio"}
          </p>

          <div
            style={{
              fontSize: "92px",
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontWeight: 300,
              color: "#1A1514",
              lineHeight: 1,
              letterSpacing: "-0.01em",
              marginBottom: "8px",
            }}
          >
            Natanael
          </div>
          <div
            style={{
              fontFamily: '"Pinyon Script", cursive',
              fontSize: "76px",
              color: "#7B1F2A",
              lineHeight: 1,
              margin: "4px 0",
            }}
          >
            &amp;
          </div>
          <div
            style={{
              fontSize: "92px",
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontWeight: 300,
              color: "#1A1514",
              lineHeight: 1,
              letterSpacing: "-0.01em",
              marginBottom: "30px",
            }}
          >
            Raluka
          </div>

          {/* Ornamental rule */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              margin: "12px auto 14px",
              maxWidth: "420px",
            }}
          >
            <div
              style={{
                flex: 1,
                height: "1px",
                background: "linear-gradient(to right, transparent, #8B5A2B, transparent)",
              }}
            />
            <span
              style={{
                fontFamily: '"Pinyon Script", cursive',
                fontSize: "38px",
                color: "#7B1F2A",
                lineHeight: 1,
              }}
            >
              09 · 08 · 2026
            </span>
            <div
              style={{
                flex: 1,
                height: "1px",
                background: "linear-gradient(to right, transparent, #8B5A2B, transparent)",
              }}
            />
          </div>

          <p
            style={{
              fontStyle: "italic",
              fontSize: "20px",
              color: "rgba(26,21,20,0.75)",
              maxWidth: "520px",
              margin: "16px auto 0",
              lineHeight: 1.55,
            }}
          >
            {t.hero.subtitle}
          </p>
        </div>

        {/* Middle section: program */}
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            gap: "60px",
            marginTop: "20px",
          }}
        >
          {[
            { time: t.program.ceremony.time, title: t.program.ceremony.title },
            { time: t.program.party.time, title: t.program.party.title },
          ].map((it) => (
            <div key={it.time} style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "44px",
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  color: "#7B1F2A",
                  lineHeight: 1,
                  marginBottom: "8px",
                }}
              >
                {it.time}
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontWeight: 300,
                  color: "#1A1514",
                }}
              >
                {it.title}
              </div>
            </div>
          ))}
        </div>

        {/* Venue */}
        <div style={{ textAlign: "center", marginTop: "32px" }}>
          <p
            style={{
              fontFamily: '"Outfit", sans-serif',
              fontSize: "10px",
              letterSpacing: "0.45em",
              textTransform: "uppercase",
              color: "#7B1F2A",
              margin: 0,
              marginBottom: "12px",
              fontWeight: 500,
            }}
          >
            {t.venue.overline}
          </p>
          <div
            style={{
              fontFamily: '"Pinyon Script", cursive',
              fontSize: "56px",
              color: "#1A1514",
              lineHeight: 1,
              marginBottom: "10px",
            }}
          >
            The Barn
          </div>
          <p
            style={{
              fontStyle: "italic",
              fontSize: "16px",
              color: "rgba(26,21,20,0.7)",
              margin: 0,
            }}
          >
            {t.venue.address}
          </p>
        </div>

        {/* Dress code */}
        <div style={{ width: "100%", textAlign: "center", marginTop: "32px" }}>
          <p
            style={{
              fontFamily: '"Outfit", sans-serif',
              fontSize: "10px",
              letterSpacing: "0.45em",
              textTransform: "uppercase",
              color: "#7B1F2A",
              margin: 0,
              marginBottom: "14px",
              fontWeight: 500,
            }}
          >
            {t.dressCode.overline}
          </p>
          <p
            style={{
              fontSize: "20px",
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              color: "#1A1514",
              fontWeight: 300,
              margin: "0 0 16px",
            }}
          >
            {t.dressCode.title}
          </p>
          <div
            style={{
              margin: "0 auto",
              maxWidth: "440px",
              height: "16px",
              borderRadius: "999px",
              background:
                "linear-gradient(90deg, #9B2434 0%, #7B1F2A 18%, #5C1820 34%, #8B5A2B 58%, #4A2C1A 78%, #1A1514 100%)",
            }}
          />
        </div>

        {/* Bottom signature */}
        <div style={{ marginTop: "24px", textAlign: "center" }}>
          <div
            style={{
              fontFamily: '"Pinyon Script", cursive',
              fontSize: "44px",
              color: "#7B1F2A",
              lineHeight: 1,
              marginBottom: "8px",
            }}
          >
            N &amp; R
          </div>
          <p
            style={{
              fontFamily: '"Outfit", sans-serif',
              fontSize: "10px",
              letterSpacing: "0.4em",
              textTransform: "uppercase",
              color: "rgba(26,21,20,0.55)",
              margin: 0,
              fontWeight: 500,
            }}
          >
            {{
              ro: "Cu drag, vă așteptăm",
              it: "Con affetto, vi aspettiamo",
              de: "Mit Liebe erwarten wir euch",
            }[lang] || "Con affetto, vi aspettiamo"}
          </p>
        </div>
      </div>
    </div>
  );
});

export default InvitationCard;
